"use client"

import React from "react"
import { useState } from "react"
import { Link, Navigate } from "react-router-dom"
import { Card, Flex, Box, Heading, Text, TextField, Button } from "@radix-ui/themes"
import { useAuth } from "../../contexts/AuthContext"
import TurboLogo from "../../components/TurboLogo"
import toast from "react-hot-toast"
import LanguageSwitcher from "../../components/LanguageSwitcher"
import { useLanguage } from "../../contexts/LanguageContext"

const LoginPage: React.FC = () => {
  const { t, language, dir } = useLanguage()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const { user, login } = useAuth()

  if (user) {
    return <Navigate to="/admin/dashboard" replace />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email || !password) {
      toast.error("Please fill in all fields")
      return
    }

    setIsLoading(true)
    try {
      await login(email, password)
      toast.success("Login successful!")
    } catch (error: any) {
      toast.error(error.message || "Login failed")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Flex 
      direction="column"
      align="center" 
      justify="center" 
      style={{ 
        minHeight: "100vh", 
        background: "linear-gradient(135deg, var(--red-2) 0%, var(--gray-1) 50%, var(--gray-2) 100%)",
        padding: "2rem",
        direction: dir
      }}
    >
      {/* Language Switcher */}
      <Box style={{ position: "absolute", top: "1rem", [language === 'ar' ? 'left' : 'right']: "1rem" }}>
        <LanguageSwitcher />
      </Box>
      
      <Card style={{ maxWidth: 450, width: "100%" }}>
        <Flex direction="column" gap="6">
          {/* Logo and Title */}
          <Flex direction="column" align="center" gap="4">
            <Box style={{ width: 64, height: 64, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <TurboLogo width={64} height={54} color="#DC2626" />
            </Box>
            <Heading size="8" align="center">{t('turboDeliveryAdmin')}</Heading>
            <Text size="2" align="center" color="gray">
              Sign in to your admin account
            </Text>
          </Flex>

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            <Flex direction="column" gap="4">
              <Box>
                <Text as="label" size="2" weight="medium" mb="1">
                  {t('emailAddress')}
                </Text>
                <TextField.Root
                  type="email"
                  placeholder={t('emailAddress')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  size="3"
                />
              </Box>

              <Box>
                <Text as="label" size="2" weight="medium" mb="1">
                  {t('password')}
                </Text>
                <TextField.Root
                  type="password"
                  placeholder={t('password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  size="3"
                />
              </Box>

              <Button 
                type="submit" 
                disabled={isLoading}
                size="3"
                style={{ width: "100%" }}
              >
                {isLoading ? t('signingIn') : t('signIn')}
              </Button>
            </Flex>
          </form>

          {/* Back to Main Site Link */}
          <Flex justify="center">
            <Link to="/" style={{ textDecoration: "none" }}>
              <Text size="2" color="gray" style={{ cursor: "pointer" }}>
                {t('backToMainSite')}
              </Text>
            </Link>
          </Flex>
        </Flex>
      </Card>
    </Flex>
  )
}

export default LoginPage
